import { Controller } from "@hotwired/stimulus"
import { Spring, prefersReducedMotion } from "../lib/spring"

// A step-by-step form: one panel at a time, an indicator that says where you
// are, buttons that move you, and a draft in sessionStorage so a reload or a
// slip doesn't lose what was typed.
//
// Panels inside a `stage` target cross-fade in place on a spring — from
// wherever they currently are, so Back mid-flight simply turns around —
// while the stage's height follows; the indicator's connectors fill as one
// continuous value. Without a stage (the newsletter wizard) panels just swap.
export default class extends Controller {
  static targets = ["step", "indicator", "backButton", "nextButton", "submitButton", "stage"]
  static values = {
    currentStep: { type: Number, default: 1 },
    totalSteps: { type: Number, default: 3 },
    minStep: { type: Number, default: 1 },
    storageKey: { type: String, default: "" },
    // Off for a run that starts from given answers (e.g. issuing another
    // like an earlier certificate): the stored draft would override them.
    resume: { type: Boolean, default: true }
  }

  connect() {
    this.panels = new Map()
    this.progress = new Spring({
      value: this.currentStepValue,
      response: 0.4,
      damping: 1,
      onUpdate: (value) => this.drawProgress(value)
    })

    if (this.resumeValue) this.restoreState()
    else this.clearState()

    this.showCurrentStep({ animate: false })
    this.element.addEventListener("keydown", this.handleKeydown)
    this.element.addEventListener("change", this.clearChoiceError)
    this.element.addEventListener("input", this.clearFieldError)
    this.element.addEventListener("input", this.reflectField)
    this.element.addEventListener("blur", this.reflectField, true)
    this.element.addEventListener("date-range-picker:change", this.clearDateError)
    this.element.querySelectorAll("[data-count-for]").forEach(counter => this.reflectField({ target: this.fieldNamed(counter.dataset.countFor) }))

    if (this.storageKeyValue) {
      this.element.addEventListener("input", this.saveState)
      this.element.addEventListener("change", this.saveState)
    }

    document.addEventListener("turbo:morph", this.sync)
    document.addEventListener("turbo:before-cache", this.settle)
    document.addEventListener("turbo:before-morph-attribute", this.keepMotionStyles)

    this.ready = true
  }

  disconnect() {
    this.ready = false
    this.element.removeEventListener("keydown", this.handleKeydown)
    this.element.removeEventListener("change", this.clearChoiceError)
    this.element.removeEventListener("input", this.clearFieldError)
    this.element.removeEventListener("input", this.reflectField)
    this.element.removeEventListener("blur", this.reflectField, true)
    this.element.removeEventListener("date-range-picker:change", this.clearDateError)
    if (this.storageKeyValue) {
      this.element.removeEventListener("input", this.saveState)
      this.element.removeEventListener("change", this.saveState)
    }
    document.removeEventListener("turbo:morph", this.sync)
    document.removeEventListener("turbo:before-cache", this.settle)
    document.removeEventListener("turbo:before-morph-attribute", this.keepMotionStyles)
    this.panels.forEach(({ spring }) => spring.stop())
    this.progress.stop()
  }

  // Enter moves on, except where it already means something: a button, a
  // select, a text area, a dialog, or a listbox that claimed it.
  handleKeydown = (event) => {
    if (event.key !== "Enter" || event.defaultPrevented) return
    const target = event.target
    if (target.closest("button, select, textarea, dialog, [role='listbox'], [contenteditable='true']")) return
    if (this.currentStepValue < this.totalStepsValue) {
      event.preventDefault()
      this.next()
    }
  }

  next() {
    if (!this.validateCurrentStep()) return
    if (this.currentStepValue < this.totalStepsValue) {
      this.currentStepValue++
      this.saveState()
    }
  }

  back() {
    if (this.currentStepValue > this.minStepValue) {
      this.currentStepValue--
      this.saveState()
    }
  }

  // Clicking a completed step in the indicator jumps
  // back to it. Only backwards: the steps ahead still have to be validated
  // in order.
  goTo(event) {
    const step = parseInt(event.currentTarget.dataset.step)
    if (step >= this.minStepValue && step < this.currentStepValue) {
      this.currentStepValue = step
      this.saveState()
    }
  }

  submit() {
    this.clearState()
  }

  // The button says what will happen: create, or create and send.
  // The moment of commit: the button says what is happening and nothing
  // else changes, so a slow network never feels like a dead click.
  committing(event) {
    const button = event.detail?.formSubmission?.submitter || this.submitButtonTargets.find(el => !el.classList.contains("hidden"))
    if (!button || button.dataset.committing) return
    button.dataset.committing = button.value || button.textContent
    const label = "Creating…"
    if (button.tagName === "INPUT") button.value = label
    else button.textContent = label
    button.setAttribute("aria-busy", "true")
    this.currentPanel?.setAttribute("aria-busy", "true")
  }

  committed(event) {
    const button = event.detail?.formSubmission?.submitter || this.element.querySelector("[data-committing]")
    if (!button?.dataset.committing) return
    if (button.tagName === "INPUT") button.value = button.dataset.committing
    else button.textContent = button.dataset.committing
    delete button.dataset.committing
    button.removeAttribute("aria-busy")
    this.currentPanel?.removeAttribute("aria-busy")
  }

  currentStepValueChanged(value, previous) {
    if (!this.panels) return
    this.showCurrentStep({ animate: this.ready, from: previous })
  }

  get currentPanel() {
    return this.panel(this.currentStepValue)
  }

  panel(step) {
    return this.stepTargets.find(el => parseInt(el.dataset.step) === step)
  }

  // --- Showing a step ---------------------------------------------------

  showCurrentStep({ animate = true, from = null } = {}) {
    const to = this.currentStepValue
    const incoming = this.panel(to)
    const outgoing = from !== null && from !== to ? this.panel(from) : null
    const fluid = animate && outgoing && incoming && this.hasStageTarget && !prefersReducedMotion()

    if (fluid) {
      this.transition(outgoing, incoming, to > from ? 1 : -1)
    } else {
      this.stepTargets.forEach(el => {
        this.resetPanel(el)
        el.classList.toggle("hidden", el !== incoming)
      })
      if (this.hasStageTarget) this.stageTarget.style.height = ""
      if (animate && outgoing) this.arrive(incoming, { smooth: false })
      this.dispatch("transition-end", { target: incoming || this.element, detail: { from, to } })
    }

    this.updateIndicators({ animate })
    this.updateButtons()
  }

  // The outgoing panel is pinned where it is and fades out; the incoming one
  // takes the flow and fades in over it. Nothing moves sideways.
  transition(outgoing, incoming, direction) {
    this.dispatch("transition-start", { target: incoming, detail: { from: parseInt(outgoing.dataset.step), to: parseInt(incoming.dataset.step), direction } })

    const stage = this.stageTarget
    const startHeight = stage.offsetHeight

    const out = this.presence(outgoing)
    outgoing.setAttribute("inert", "")
    outgoing.setAttribute("data-motion", "")
    Object.assign(outgoing.style, { position: "absolute", top: "0", left: "0", right: "0" })
    out.spring.onRest = () => {
      if (out.spring.value !== 0) return
      this.resetPanel(outgoing)
      outgoing.classList.add("hidden")
    }
    out.spring.to(0)

    const inc = this.presence(incoming)
    incoming.classList.remove("hidden")
    incoming.removeAttribute("inert")
    incoming.setAttribute("data-motion", "")
    Object.assign(incoming.style, { position: "", top: "", left: "", right: "" })
    this.drawPanel(incoming, inc)

    // Two frames: the incoming panel needs real width before anything inside
    // it (the calendar) can size itself, and only then is its height honest.
    stage.style.height = `${startHeight}px`
    let endHeight = null
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (!inc.spring.animating) return
      const previous = stage.style.height
      stage.style.height = ""
      endHeight = incoming.offsetHeight
      stage.style.height = previous
    }))

    inc.spring.onUpdate = (value) => {
      this.drawPanel(incoming, inc)
      if (endHeight !== null) stage.style.height = `${startHeight + (endHeight - startHeight) * value}px`
    }
    inc.spring.onRest = () => {
      if (inc.spring.value !== 1) return
      this.resetPanel(incoming)
      stage.style.height = ""
      this.dispatch("transition-end", { target: incoming, detail: { from: parseInt(outgoing.dataset.step), to: parseInt(incoming.dataset.step), direction } })
    }
    inc.spring.to(1)

    this.arrive(incoming, { smooth: true })
  }

  presence(panel) {
    if (!this.panels.has(panel)) {
      const entry = { spring: new Spring({ value: panel.classList.contains("hidden") ? 0 : 1, response: 0.35, damping: 1 }) }
      entry.spring.onUpdate = () => this.drawPanel(panel, entry)
      this.panels.set(panel, entry)
    }
    return this.panels.get(panel)
  }

  drawPanel(panel, { spring }) {
    panel.style.opacity = String(Math.max(0, Math.min(1, spring.value)))
  }

  resetPanel(panel) {
    panel.removeAttribute("inert")
    panel.removeAttribute("data-motion")
    panel.style.removeProperty("opacity")
    panel.style.removeProperty("position")
    panel.style.removeProperty("top")
    panel.style.removeProperty("left")
    panel.style.removeProperty("right")
  }

  // Bring the indicator back into view and hand focus to the step's heading,
  // so "where am I" is answered for eyes and screen readers alike.
  arrive(panel, { smooth }) {
    const top = this.element.getBoundingClientRect().top
    if (top < 0) window.scrollTo({ top: window.scrollY + top - 16, behavior: smooth && !prefersReducedMotion() ? "smooth" : "auto" })
    const heading = [ ...panel.querySelectorAll("h2[tabindex='-1']") ].find(h2 => h2.offsetParent !== null)
    heading?.focus({ preventScroll: true })
  }

  // --- Indicator ---------------------------------------------------------

  updateIndicators({ animate = true } = {}) {
    const current = this.currentStepValue
    this.indicatorTargets.forEach(el => {
      const step = parseInt(el.dataset.step)
      if (el.tagName === "BUTTON") el.disabled = step >= current
      if (step === current) el.setAttribute("aria-current", "step")
      else el.removeAttribute("aria-current")
    })

    if (animate && !prefersReducedMotion()) {
      this.progress.to(current)
    } else {
      this.progress.stop()
      this.progress.value = this.progress.target = current
      this.drawProgress(current)
    }

    // Phones only show the circles, so name the step underneath them; the
    // same line is the live region that announces each step everywhere.
    const mobileLabel = this.element.querySelector("[data-role='mobile-label']")
    const active = this.indicatorTargets.find(el => parseInt(el.dataset.step) === current)
    if (mobileLabel && active) {
      mobileLabel.textContent = `Step ${current} of ${this.indicatorTargets.length} · ${active.dataset.label}`
    }
  }

  // One number, 1..n, drives every connector and circle. Going forward the
  // line runs to the next circle before it lights; going back the circles
  // step down at once and the line drains behind them.
  drawProgress(progress) {
    const current = this.currentStepValue
    this.element.querySelectorAll("[data-role='fill']").forEach(fill => {
      const after = parseInt(fill.dataset.afterStep)
      fill.style.transform = `scaleX(${Math.max(0, Math.min(1, progress - after)).toFixed(4)})`
    })
    this.indicatorTargets.forEach(el => {
      const step = parseInt(el.dataset.step)
      let state
      if (step < current) state = progress >= step + 1 - 0.001 ? "done" : "current"
      else if (step === current) state = progress >= step - 0.001 ? "current" : "todo"
      else state = "todo"
      if (el.dataset.state !== state) el.dataset.state = state
    })
  }

  updateButtons() {
    this.backButtonTargets.forEach(el => el.classList.toggle("hidden", this.currentStepValue === this.minStepValue))
    const isLastStep = this.currentStepValue === this.totalStepsValue
    this.nextButtonTargets.forEach(el => el.classList.toggle("hidden", isLastStep))
    this.submitButtonTargets.forEach(el => el.classList.toggle("hidden", !isLastStep))
  }

  // --- Turbo -------------------------------------------------------------

  // A morph (the 422 re-render) rewrites inline styles under our feet; a
  // cache snapshot must never hold a mid-flight frame. Both: settle
  // everything and redraw the state we know.
  sync = () => {
    this.settle()
    this.showCurrentStep({ animate: false })
    this.element.querySelector("[aria-invalid='true']")?.focus({ preventScroll: true })
  }

  settle = () => {
    this.panels.forEach(({ spring }, panel) => { spring.stop(); this.resetPanel(panel) })
    this.progress.stop()
    if (this.hasStageTarget) this.stageTarget.style.height = ""
  }

  keepMotionStyles = (event) => {
    if (event.detail?.attributeName === "style" && event.target?.hasAttribute?.("data-motion")) event.preventDefault()
  }

  // --- Validation ----------------------------------------------------------

  validateCurrentStep() {
    const currentPanel = this.currentPanel
    if (!currentPanel) return true

    let valid = true
    let first = null

    currentPanel.querySelectorAll("[aria-invalid='true']").forEach(el => this.clearInvalid(el))

    currentPanel.querySelectorAll("input:not([type='hidden']):not([type='radio']):not([type='checkbox'])[required], select[required], textarea[required]").forEach(field => {
      if (!field.value?.trim() || !field.validity.valid) {
        this.markInvalid(field)
        first ||= field
        valid = false
      }
    })

    // A group of radios (the certificate choice) is valid once any one is picked;
    // the group's fieldset carries the error since the radios are hidden.
    currentPanel.querySelectorAll("[data-required-choice]").forEach(group => {
      if (!group.querySelector("input[type='radio']:checked")) {
        this.markInvalid(group)
        first ||= group.querySelector("label") || group
        valid = false
      }
    })

    const startField = currentPanel.querySelector("[data-date-range-picker-target='start']")
    const endField = currentPanel.querySelector("[data-date-range-picker-target='end']")
    if (startField && endField && (!startField.value || !endField.value)) {
      const display = this.dateDisplay(currentPanel)
      if (display) { this.markInvalid(display, "dates"); first ||= display }
      valid = false
    }

    if (first) first.focus({ preventScroll: false })
    return valid
  }

  // Picking an option answers the "choose one" error straight away rather than
  // leaving the red ring until the next attempt to move on.
  clearChoiceError = (event) => {
    const group = event.target.closest?.("[data-required-choice]")
    if (!group || event.target.type !== "radio") return
    this.clearInvalid(group)
  }

  // So does typing something valid into a field, or picking the dates.
  clearFieldError = (event) => {
    const field = event.target
    if (field.getAttribute?.("aria-invalid") !== "true") return
    if (field.value?.trim() && field.validity?.valid !== false) this.clearInvalid(field)
  }

  clearDateError = (event) => {
    const display = this.dateDisplay(event.target)
    if (display?.getAttribute("aria-invalid") === "true" && event.detail?.start && event.detail?.end) this.clearInvalid(display, "dates")
  }

  // flatpickr hides the real display field behind the one it draws, so that
  // is the one to mark.
  dateDisplay(within) {
    const display = within.querySelector("[data-date-range-picker-target='display']")
    return display?._flatpickr?.altInput || display
  }

  // Status, completion and warning, as the field is typed in: a counter
  // for fields that carry one, a tick once a field is filled and valid, an
  // amber edge when the name is about to run out of room.
  reflectField = (event) => {
    const field = event.target
    if (!field?.name) return
    const counter = this.element.querySelector(`[data-count-for="${CSS.escape(field.name)}"]`)
    const max = parseInt(field.getAttribute("maxlength"))
    if (counter && max) {
      const left = max - field.value.length
      counter.textContent = left <= 4 ? `${left} character${left === 1 ? "" : "s"} left` : `${field.value.length}/${max}`
      field.toggleAttribute("data-warn", left <= 4)
    }
    field.toggleAttribute("data-valid", Boolean(field.value?.trim()) && field.validity?.valid !== false && field.getAttribute("aria-invalid") !== "true")
  }

  fieldNamed(name) {
    return this.element.querySelector(`[name="${CSS.escape(name)}"]`)
  }

  markInvalid(field, name = field.name || field.dataset.errorName) {
    field.classList.remove("ring-1", "ring-primary", "ring-transparent")
    field.classList.add("ring-2", "ring-red-500")
    field.setAttribute("aria-invalid", "true")
    field.removeAttribute("data-valid")
    const message = this.errorFor(name)
    if (message) {
      message.hidden = false
      if (message.id) field.setAttribute("aria-describedby", message.id)
    }
    field.removeAttribute("data-nudge")
    void field.offsetWidth
    field.setAttribute("data-nudge", "")
  }

  clearInvalid(field, name = field.name || field.dataset.errorName) {
    field.classList.remove("ring-2", "ring-red-500", "ring-1", "ring-transparent")
    field.classList.add("ring-1", field.hasAttribute("data-required-choice") ? "ring-transparent" : "ring-primary")
    field.removeAttribute("aria-invalid")
    field.removeAttribute("data-nudge")
    field.removeAttribute("aria-describedby")
    const message = this.errorFor(name)
    if (message) message.hidden = true
  }

  errorFor(name) {
    return name ? this.element.querySelector(`[data-error-for="${CSS.escape(name)}"]`) : null
  }

  // --- Draft in sessionStorage ------------------------------------------

  saveState = () => {
    if (!this.storageKeyValue) return

    const form = this.element.querySelector("form") || this.element
    const data = { step: this.currentStepValue, fields: {} }
    for (const [key, value] of new FormData(form).entries()) {
      if (key !== "authenticity_token" && typeof value === "string") data.fields[key] = value
    }
    sessionStorage.setItem(this.storageKeyValue, JSON.stringify(data))
  }

  restoreState() {
    if (!this.storageKeyValue) return

    const stored = sessionStorage.getItem(this.storageKeyValue)
    if (!stored) return

    try {
      const data = JSON.parse(stored)
      const form = this.element.querySelector("form") || this.element

      Object.entries(data.fields || {}).forEach(([key, value]) => {
        const field = form.querySelector(`[name="${CSS.escape(key)}"]`)
        if (!field) return
        if (field.type === "radio" || field.type === "checkbox") {
          const option = form.querySelector(`[name="${CSS.escape(key)}"][value="${CSS.escape(value)}"]`)
          if (option) option.checked = true
        } else {
          field.value = value
        }
      })

      if (data.step && data.step >= this.minStepValue && data.step <= this.totalStepsValue) {
        this.currentStepValue = data.step
      }
    } catch {
      sessionStorage.removeItem(this.storageKeyValue)
    }
  }

  clearState() {
    if (this.storageKeyValue) sessionStorage.removeItem(this.storageKeyValue)
  }
}
